import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Loader2, Plus, Trash2, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const normalizeList = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  return [];
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount || 0);
};

export default function SalesReturnsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [variants, setVariants] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [returns, setReturns] = useState([]);

  const [form, setForm] = useState({
    customer_id: '',
    reason_id: '',
    order_id: '',
    return_date: '',
    notes: ''
  });

  const [items, setItems] = useState([
    { variant_id: '', qty: '', unit_price: '', batch_no: '', note: '' }
  ]);

  const variantOptions = useMemo(() => normalizeList(variants), [variants]);
  const reasonOptions = useMemo(() => normalizeList(reasons).filter((r) => r.is_active), [reasons]);

  const totalAmount = useMemo(() => {
    return items.reduce((sum, item) => {
      const qty = parseFloat(item.qty || '0');
      const price = parseFloat(item.unit_price || '0');
      return sum + (Number.isFinite(qty * price) ? qty * price : 0);
    }, 0);
  }, [items]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [customerRes, variantRes, reasonRes, returnRes] = await Promise.all([
        adminApi.getCustomers(),
        adminApi.getProductVariants(),
        adminApi.getReturnReasons(),
        adminApi.getSalesReturns()
      ]);
      setCustomers(normalizeList(customerRes.data));
      setVariants(normalizeList(variantRes.data));
      setReasons(normalizeList(reasonRes.data));
      setReturns(normalizeList(returnRes.data));
    } catch (error) {
      toast.error('Failed to load sales returns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleAddItem = () => {
    setItems((prev) => [...prev, { variant_id: '', qty: '', unit_price: '', batch_no: '', note: '' }]);
  };

  const handleRemoveItem = (index) => {
    setItems((prev) => prev.filter((_, idx) => idx !== index));
  };

  const updateItem = (index, field, value) => {
    setItems((prev) => prev.map((item, idx) => (idx === index ? { ...item, [field]: value } : item)));
  };

  const handleSave = async () => {
    if (!form.customer_id) {
      toast.error('Select a customer');
      return;
    }
    if (!form.reason_id) {
      toast.error('Select a return reason');
      return;
    }
    if (items.length === 0) {
      toast.error('Add at least one item');
      return;
    }
    const invalid = items.some((item) => !item.variant_id || !item.qty || !item.unit_price);
    if (invalid) {
      toast.error('Enter variant, quantity, and rate for all items');
      return;
    }

    setSaving(true);
    try {
      await adminApi.createSalesReturn({
        ...form,
        items: items.map((item) => ({
          variant_id: item.variant_id,
          qty: parseFloat(item.qty),
          unit_price: parseFloat(item.unit_price),
          batch_no: item.batch_no || undefined,
          note: item.note || undefined
        }))
      });
      toast.success('Sales return created');
      setForm({ customer_id: '', reason_id: '', order_id: '', return_date: '', notes: '' });
      setItems([{ variant_id: '', qty: '', unit_price: '', batch_no: '', note: '' }]);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create sales return');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="sales-returns-page">
      <div>
        <h1 className="text-3xl font-bold text-[#2D2420]">Sales Returns</h1>
        <p className="text-[#A8A8A8] mt-1">Create return invoices and credit notes</p>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader>
          <CardTitle className="text-lg text-[#2D2420]">New Sales Return</CardTitle>
          <CardDescription>Returns are posted to the default return warehouse and credit note is generated.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Customer</Label>
              <Select value={form.customer_id} onValueChange={(value) => setForm((prev) => ({ ...prev, customer_id: value }))}>
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.shop_name || customer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Return Reason</Label>
              <Select value={form.reason_id} onValueChange={(value) => setForm((prev) => ({ ...prev, reason_id: value }))}>
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {reasonOptions.map((reason) => (
                    <SelectItem key={reason.id} value={reason.id}>{reason.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Order ID (optional)</Label>
              <Input
                value={form.order_id}
                onChange={(e) => setForm((prev) => ({ ...prev, order_id: e.target.value }))}
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Return Date</Label>
              <Input
                type="date"
                value={form.return_date}
                onChange={(e) => setForm((prev) => ({ ...prev, return_date: e.target.value }))}
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-[#5C4033]">Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
              className="border-[#E5E5E5] focus:border-[#C97A5E]"
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-[#5C4033]">Return Items</Label>
              <Button variant="outline" onClick={handleAddItem}>
                <Plus className="w-4 h-4 mr-2" /> Add Item
              </Button>
            </div>

            <div className="space-y-3">
              {items.map((item, index) => (
                <div key={`return-item-${index}`} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                  <div className="md:col-span-4 space-y-2">
                    <Label className="text-[#5C4033]">Variant</Label>
                    <Select value={item.variant_id} onValueChange={(value) => updateItem(index, 'variant_id', value)}>
                      <SelectTrigger className="border-[#E5E5E5]">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {variantOptions.map((variant) => (
                          <SelectItem key={variant.id} value={variant.id}>
                            {variant.master_name} - {variant.pack_size_label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <Label className="text-[#5C4033]">Qty</Label>
                    <Input
                      type="number"
                      value={item.qty}
                      onChange={(e) => updateItem(index, 'qty', e.target.value)}
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                    />
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <Label className="text-[#5C4033]">Rate</Label>
                    <Input
                      type="number"
                      value={item.unit_price}
                      onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                    />
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <Label className="text-[#5C4033]">Batch No</Label>
                    <Input
                      value={item.batch_no}
                      onChange={(e) => updateItem(index, 'batch_no', e.target.value)}
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                    />
                  </div>
                  <div className="md:col-span-1 space-y-2">
                    <Label className="text-[#5C4033]">Amount</Label>
                    <div className="px-3 py-2 rounded-md border border-[#E5E5E5] text-sm text-[#2D2420]">
                      {formatCurrency((parseFloat(item.qty || '0') || 0) * (parseFloat(item.unit_price || '0') || 0))}
                    </div>
                  </div>
                  <div className="md:col-span-1 flex justify-end">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveItem(index)}
                      disabled={items.length === 1}
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="md:col-span-12">
                    <Input
                      value={item.note}
                      onChange={(e) => updateItem(index, 'note', e.target.value)}
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                      placeholder="Item note (optional)"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-[#E5E5E5] pt-4">
            <div className="text-sm text-[#2D2420]">
              Total Amount: <span className="font-semibold">{formatCurrency(totalAmount)}</span>
            </div>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#C97A5E] hover:bg-[#B06850]"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Return
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader>
          <CardTitle className="text-lg text-[#2D2420]">Recent Returns</CardTitle>
          <CardDescription>Download credit notes from recent returns</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
            </div>
          ) : returns.length === 0 ? (
            <div className="p-6 text-sm text-[#A8A8A8]">No sales returns yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Return No</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Customer</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Reason</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Amount</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Credit Note</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {returns.map((entry) => (
                    <tr key={entry.id}>
                      <td className="py-3 px-4 text-sm text-[#2D2420]">{entry.return_no}</td>
                      <td className="py-3 px-4 text-sm text-[#2D2420]">{customers.find((c) => c.id === entry.customer_id)?.shop_name || customers.find((c) => c.id === entry.customer_id)?.name || '-'}</td>
                      <td className="py-3 px-4 text-sm text-[#2D2420]">{entry.reason}</td>
                      <td className="py-3 px-4 text-sm text-right text-[#2D2420]">{formatCurrency(entry.total_amount)}</td>
                      <td className="py-3 px-4 text-sm text-[#2D2420]">{entry.credit_note_no || '-'}</td>
                      <td className="py-3 px-4 text-right">
                        {entry.credit_note_url ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(entry.credit_note_url, '_blank')}
                          >
                            <Download className="w-4 h-4 mr-2" /> Download
                          </Button>
                        ) : (
                          <span className="text-xs text-[#A8A8A8]">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
